/**
 * Browser-Side Encryption Service
 * Encrypts data in sessionStorage/localStorage using AES-256-GCM
 * HIPAA Compliant - Protects PHI at rest in browser storage
 */

class BrowserEncryptionService {
  constructor() {
    this.encryptionKey = null;
    this.isInitialized = false;
  }

  /**
   * Initialize encryption service
   * Generates ephemeral encryption key for the session
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Generate a session-specific encryption key using Web Crypto API
      // This key is ephemeral and only exists in memory during the session
      this.encryptionKey = await window.crypto.subtle.generateKey(
        {
          name: 'AES-GCM',
          length: 256
        },
        false, // Not extractable - more secure
        ['encrypt', 'decrypt']
      );

      this.isInitialized = true;
      console.info('[Encryption] ✅ Browser encryption initialized with AES-256-GCM');
    } catch (error) {
      console.error('[Encryption] Failed to initialize:', error);
      throw new Error('Failed to initialize encryption service');
    }
  }

  /**
   * Encrypt data using AES-256-GCM
   */
  async encrypt(plaintext) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(plaintext);
      const iv = window.crypto.getRandomValues(new Uint8Array(12));

      const encryptedBuffer = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv, tagLength: 128 },
        this.encryptionKey,
        data
      );

      // Combine IV + encrypted data
      const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedBuffer), iv.length);

      return this.arrayBufferToBase64(combined);
    } catch (error) {
      console.error('[Encryption] Encryption failed:', error);
      throw error;
    }
  }

  /**
   * Decrypt data using AES-256-GCM
   */
  async decrypt(encryptedData) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const combined = this.base64ToArrayBuffer(encryptedData);
      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      const decryptedBuffer = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv, tagLength: 128 },
        this.encryptionKey,
        data
      );

      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error('[Encryption] Decryption failed:', error);
      throw error;
    }
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Securely store data in sessionStorage (encrypted)
   */
  async setSessionItem(key, value) {
    try {
      const encrypted = await this.encrypt(JSON.stringify(value));
      sessionStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('[Encryption] Failed to store encrypted session data:', error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt data from sessionStorage
   */
  async getSessionItem(key) {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) return null;

      const decrypted = await this.decrypt(encrypted);
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('[Encryption] Failed to retrieve encrypted session data:', error);
      sessionStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Securely store data in localStorage (encrypted)
   */
  async setLocalItem(key, value) {
    try {
      const encrypted = await this.encrypt(JSON.stringify(value));
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('[Encryption] Failed to store encrypted local data:', error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt data from localStorage
   */
  async getLocalItem(key) {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;

      const decrypted = await this.decrypt(encrypted);
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('[Encryption] Failed to retrieve encrypted local data:', error);
      localStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Clear all encryption keys from memory
   */
  cleanup() {
    this.encryptionKey = null;
    this.isInitialized = false;
    console.info('[Encryption] Encryption keys cleared from memory');
  }
}

const encryptionService = new BrowserEncryptionService();
export default encryptionService;
